/**
 * Neutral full-height loading shell for the chats route. Two things render it
 * (both in `UnifiedChatsV2Entry`): the thread-existence probe that decides
 * resume-vs-fresh-seed, and the `dynamic({ ssr: false })` BrainChatPage chunk
 * load.
 *
 * Deliberately inert: it renders no chat tree, so it fires no analytics and
 * consumes no submit-start timestamp. That is what lets it be held on safely —
 * the old flag gate used to mount the v1 tree speculatively while waiting, and
 * that fired a garbage `Dupe AI Chat First Result` and raced
 * `consumeSubmitStart`, starving the v2 latency events. (That flag gate is
 * gone as of DUP-2069; the inertness still matters for the probe.)
 *
 * `holdReason` names WHICH of those is still outstanding (DUP-2215). It's
 * optional — all three current callers pass one, but nothing enforces that a
 * future caller must — and outside production a reason still outstanding past
 * this shell's threshold is printed on the page and warned to the console.
 * A caller whose wait legitimately runs long sets its own via `stallMs`.
 * That is the
 * whole point: a shell that never lifts is otherwise indistinguishable from a
 * slow one — it renders no error, requests nothing, and logs nothing, so the
 * only way to tell "waiting on the router" from "waiting on a probe that never
 * settles" was to add temporary logging by hand. In production the prop is
 * inert; the shell looks and behaves exactly as before.
 */
import { useEffect, useState } from 'react'

/**
 * Default threshold, for the entry conditions — the router, the thread probe,
 * an in-flight navigation. None of those compiles anything, so four seconds of
 * one is already abnormal.
 */
const STALL_MS = 4000

/**
 * Threshold for the `next/dynamic` callers. A cold dev compile is a real reason
 * to be waiting, and flagging it would be the cry-wolf this is meant to avoid.
 * Measured 2026-09-05 with `apps/web/.next` deleted: the document itself took
 * 10.1s while the chunk arrived 177ms after it, so the shell held ~350ms — but
 * that is one machine, and the point of the ceiling is the machines it isn't.
 */
export const CHUNK_STALL_MS = 15000

export function ChatPageShell({
  holdReason,
  stallMs = STALL_MS,
}: {
  holdReason?: string
  stallMs?: number
} = {}) {
  const [stalledOn, setStalledOn] = useState<string | null>(null)

  useEffect(() => {
    // Reset BEFORE the guard: a shell whose reason clears while it stays
    // mounted must drop the banner, not keep showing a wait that has ended.
    setStalledOn(null)
    if (process.env.NODE_ENV === 'production') return
    if (!holdReason) return
    const timer = setTimeout(() => {
      setStalledOn(holdReason)
      console.warn(
        `[chats] loading shell still held after ${stallMs}ms — waiting on: ${holdReason} (DUP-2215)`
      )
    }, stallMs)
    return () => clearTimeout(timer)
  }, [holdReason, stallMs])

  return (
    <div
      data-testid='page-loader'
      className='flex min-h-screen flex-col items-center justify-center gap-2 text-sm text-muted-foreground'
    >
      <span className='opacity-60'>Loading chat…</span>
      {stalledOn ? (
        <span
          data-testid='page-loader-stalled'
          className='max-w-md text-center font-mono text-xs text-red-500'
        >
          stuck &gt;{STALL_MS / 1000}s on: {stalledOn}
        </span>
      ) : null}
    </div>
  )
}

export default ChatPageShell
